import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';


/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — editable handlebars templates
   Inner HTML of `.pbd`. Top-level children are flow blocks; the
   block marked [data-split] (line-item table) is split across
   pages. Fields carrying data-edit="path" are click-to-edit in the
   preview (WYSIWYG) — they write back to the document data, never
   to this HTML. Raw HTML stays available in Advanced mode.
   Exposes PBTemplates
   ───────────────────────────────────────────────────────────── */

  // editable-field attribute helper
  const E = (path, type) => `data-edit="${path}"${type ? ` data-edit-type="${type}"` : ''}`;

  const LETTERHEAD = `<div class="pbd-lh" data-block="header" data-label="Letterhead">
  {{#if showLogo}}<img class="pbd-logo" src="assets/ERPForce_Primary-Logo.svg" alt="" />{{/if}}
  <div class="pbd-lh-co">
    <div class="pbd-co-name" ${E('company.name')}>{{company.name}}</div>
    <div class="pbd-co-line" ${E('company.line1')}>{{company.line1}}</div>
    <div class="pbd-co-line" ${E('company.line2')}>{{company.line2}}</div>
    <div class="pbd-co-line"><span ${E('company.phone')}>{{company.phone}}</span> · <span ${E('company.email')}>{{company.email}}</span></div>
    <div class="pbd-co-line">TRN <span ${E('company.trn')}>{{company.trn}}</span></div>
  </div>`;

  const FOOT = `{{#if showFooter}}<div class="pbd-foot" data-block="foot" data-label="Footer">
  <span>{{company.name}} · TRN {{company.trn}}</span>
  <span ${E('footerNote')}>{{footerNote}}</span>
</div>{{/if}}`;

  const ITEMS_HEAD = `<thead><tr>
        <th class="l">#</th><th class="l">Description</th>
        <th>Qty</th><th>Unit</th><th>Unit price</th><th>Disc</th><th>VAT</th><th>Amount</th>
      </tr></thead>`;
  const ITEMS_ROW = `<tr>
          <td class="l it-no">{{@number}}</td>
          <td class="l it-desc"><b ${E('items.{{@index}}.name')}>{{this.name}}</b><span class="it-sku"><span ${E('items.{{@index}}.sku')}>{{this.sku}}</span>{{#if this.detail}} · <span ${E('items.{{@index}}.detail')}>{{this.detail}}</span>{{/if}}</span></td>
          <td ${E('items.{{@index}}.qty', 'num')}>{{qty this.qty}}</td>
          <td ${E('items.{{@index}}.unit')}>{{this.unit}}</td>
          <td ${E('items.{{@index}}.price', 'money')}>{{money this.price}}</td>
          <td class="it-disc">{{#if this.disc}}{{pct this.disc}}{{else}}—{{/if}}</td>
          <td class="it-tax">{{pct this.tax}}</td>
          <td><b>{{money this.amount}}</b></td>
        </tr>`;

  const TOTALS = `<div class="pbd-totals-wrap" data-block="totals" data-label="Totals">
  <div class="pbd-totals">
    <div class="pbd-tot-row"><span>Subtotal</span><span class="pbd-tot-v" ${E('gross_amount')}>{{gross_amount}}</span></div>
    {{#if discount_amount}}<div class="pbd-tot-row pbd-tot-row--disc"><span>Discount</span><span class="pbd-tot-v" ${E('discount_amount')}>− {{discount_amount}}</span></div>{{/if}}
    <div class="pbd-tot-row pbd-tot-row--sub"><span>VAT (5%)</span><span class="pbd-tot-v" ${E('tax_amount')}>{{tax_amount}}</span></div>
    <div class="pbd-tot-grand"><span class="pbd-tot-k">Grand total</span><span class="pbd-tot-v" ${E('total_invoice_amount')}>{{total_invoice_amount}}</span></div>
    {{#if amount_due_words}}<div class="pbd-amountwords">In words: <b ${E('amount_due_words')}>{{amount_due_words}}</b></div>{{/if}}
  </div>
</div>`;

  const PARTY_CUST = (h) => `<div class="pbd-party">
    <div class="pbd-party-h">${h}</div>
    <div class="pbd-party-name" ${E('customer_name')}>{{customer_name}}</div>
    {{#if invoice_addresses.contact_person_data.contact_name}}<div class="pbd-party-line">Attn: <span ${E('invoice_addresses.contact_person_data.contact_name')}>{{invoice_addresses.contact_person_data.contact_name}}</span></div>{{/if}}
    <div class="pbd-party-line" ${E('invoice_addresses.billing_address_data.address')}>{{invoice_addresses.billing_address_data.address}}</div>
    {{#if customer_trn}}<div class="pbd-party-line">TRN <span ${E('customer_trn')}>{{customer_trn}}</span></div>{{/if}}
  </div>`;

  const NOTES = (termsLabel) => `<div class="pbd-cols" data-block="notes" data-label="Notes & terms">
  <div>
    <div class="pbd-notes-h">Notes</div>
    <div class="pbd-notes-body" ${E('notes')}>{{notes}}</div>
  </div>
  {{#if showTerms}}<div>
    <div class="pbd-notes-h">${termsLabel}</div>
    <div class="pbd-notes-body"><ol>{{#each terms}}<li ${E('terms.{{@index}}')}>{{this}}</li>{{/each}}</ol></div>
  </div>{{/if}}
</div>`;

  const SIGN = `<div class="pbd-sign" data-block="sign" data-label="Signature">
  <div class="pbd-sign-line"><div class="pbd-sign-rule"><b ${E('signatory.name')}>{{signatory.name}}</b><span ${E('signatory.role')}>{{signatory.role}}</span></div></div>
  <div class="pbd-sign-line"><div class="pbd-sign-rule"><b>&nbsp;</b>Customer acceptance · date</div></div>
</div>`;

  const TEMPLATES = {

    quotation:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype">Quotation</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref" ${E('doc.date')}>{{doc.date}}</div>
    <span class="pbd-status pbd-status--draft">Valid to {{doc.validUntil}}</span>
  </div>
</div>

<div class="pbd-parties pbd-parties--2" data-block="parties" data-label="Customer">
  ${PARTY_CUST('Quotation for')}
  <div class="pbd-party">
    <div class="pbd-party-h">Prepared by</div>
    <div class="pbd-party-name" ${E('doc.salesRep')}>{{doc.salesRep}}</div>
    <div class="pbd-party-line">{{company.name}}</div>
    <div class="pbd-party-line">{{company.email}}</div>
  </div>
</div>

<div class="pbd-meta pbd-meta--3" data-block="meta" data-label="Meta">
  <div class="pbd-meta-item"><span class="pbd-meta-k">Quote no.</span><span class="pbd-meta-v" ${E('doc.number')}>{{doc.number}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Date</span><span class="pbd-meta-v" ${E('doc.date')}>{{doc.date}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Valid until</span><span class="pbd-meta-v" ${E('doc.validUntil')}>{{doc.validUntil}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Customer ref</span><span class="pbd-meta-v" ${E('doc.reference')}>{{doc.reference}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Sales rep</span><span class="pbd-meta-v" ${E('doc.salesRep')}>{{doc.salesRep}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Currency</span><span class="pbd-meta-v" ${E('currency')}>{{currency}}</span></div>
</div>

<div class="pbd-items-wrap" data-block="items" data-label="Line items" data-split>
  <table class="pbd-items">
    ${ITEMS_HEAD}
    <tbody>
      {{#each items}}${ITEMS_ROW}{{/each}}
    </tbody>
  </table>
</div>

${TOTALS}

${NOTES('Terms &amp; conditions')}

${SIGN}

${FOOT}`,

    invoice:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype">Tax Invoice</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref" ${E('doc.date')}>{{doc.date}}</div>
    {{#if doc.status}}<span class="pbd-status pbd-status--due">Payment due {{doc.dueDate}}</span>{{/if}}
  </div>
</div>

<div class="pbd-parties pbd-parties--2" data-block="parties" data-label="Bill to">
  ${PARTY_CUST('Bill to')}
  {{#if showBank}}<div class="pbd-party">
    <div class="pbd-party-h">Pay to</div>
    <div class="pbd-party-name" ${E('bank.name')}>{{bank.name}}</div>
    <div class="pbd-party-line" ${E('bank.account')}>{{bank.account}}</div>
    <div class="pbd-party-line">IBAN <span ${E('bank.iban')}>{{bank.iban}}</span></div>
    <div class="pbd-party-line">SWIFT <span ${E('bank.swift')}>{{bank.swift}}</span></div>
  </div>{{/if}}
</div>

<div class="pbd-meta" data-block="meta" data-label="Meta">
  <div class="pbd-meta-item"><span class="pbd-meta-k">Invoice no.</span><span class="pbd-meta-v" ${E('doc.number')}>{{doc.number}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Issue date</span><span class="pbd-meta-v" ${E('doc.date')}>{{doc.date}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Due date</span><span class="pbd-meta-v" ${E('doc.dueDate')}>{{doc.dueDate}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">PO number</span><span class="pbd-meta-v" ${E('doc.po')}>{{doc.po}}</span></div>
</div>

<div class="pbd-items-wrap" data-block="items" data-label="Line items" data-split>
  <table class="pbd-items">
    ${ITEMS_HEAD}
    <tbody>
      {{#each items}}${ITEMS_ROW}{{/each}}
    </tbody>
  </table>
</div>

${TOTALS}

<div class="pbd-cols" data-block="notes" data-label="Notes & payment">
  <div>
    <div class="pbd-notes-h">Notes</div>
    <div class="pbd-notes-body" ${E('notes')}>{{notes}}</div>
  </div>
  {{#if showBank}}<div>
    <div class="pbd-notes-h">Payment terms · {{doc.terms}}</div>
    <div class="pbd-pay"><span class="k">Bank</span><b ${E('bank.name')}>{{bank.name}}</b><br><span class="k">IBAN</span><span ${E('bank.iban')}>{{bank.iban}}</span><br><span class="k">SWIFT</span><span ${E('bank.swift')}>{{bank.swift}}</span></div>
  </div>{{/if}}
</div>

${SIGN}

${FOOT}`,

    invoice_ksa:
`<div class="kx-head" data-block="header" data-label="Document title">
  <div class="kx-head-row">
    <div class="kx-title-block">
      <span class="kx-title">Tax Invoice</span>
      <span class="kx-title-dot"></span>
      <span class="kx-title-ar">فاتورة ضريبية</span>
    </div>
    <div class="kx-head-tag">ZATCA<br>e-Invoice</div>
  </div>
  <div class="kx-head-no">
    <div class="kx-head-no-en"><span class="kx-k">Invoice No.</span><b ${E('series_number')}>{{series_number}}</b></div>
    <div class="kx-head-no-ar" dir="rtl"><span class="kx-k">رقم الفاتورة</span><b>{{series_number}}</b></div>
  </div>
</div>

<div class="kx-parties" data-block="parties" data-label="Seller & Buyer">
  <div class="kx-party">
    <div class="kx-party-bar"><span>Seller Details</span><span class="kx-ar">تفاصيل المورد</span></div>
    <div class="kx-party-grid">
      <div class="kx-col-en">
        <div class="kx-name" ${E('company_data.name')}>{{company_data.name}}</div>
        <div class="kx-line" ${E('company_data.address')}>{{company_data.address}}</div>
        <div class="kx-kv"><span>CRN</span><b ${E('company_data.crn')}>{{company_data.crn}}</b></div>
        <div class="kx-kv"><span>VAT No.</span><b ${E('company_data.vat_number')}>{{company_data.vat_number}}</b></div>
      </div>
      <div class="kx-col-ar" dir="rtl">
        <div class="kx-name" ${E('company_data.name_ar')}>{{company_data.name_ar}}</div>
        <div class="kx-line" ${E('company_data.address_ar')}>{{company_data.address_ar}}</div>
        <div class="kx-kv"><span>السجل التجاري</span><b>{{company_data.crn}}</b></div>
        <div class="kx-kv"><span>الرقم الضريبي</span><b>{{company_data.vat_number}}</b></div>
      </div>
    </div>
  </div>
  <div class="kx-party">
    <div class="kx-party-bar"><span>Buyer Details</span><span class="kx-ar">تفاصيل العميل</span></div>
    <div class="kx-party-grid">
      <div class="kx-col-en">
        <div class="kx-name" ${E('customer_data.name')}>{{customer_data.name}}</div>
        <div class="kx-line" ${E('customer_data.email')}>{{customer_data.email}}</div>
        <div class="kx-kv"><span>CRN</span><b ${E('vendor_details_full.crn')}>{{vendor_details_full.crn}}</b></div>
        <div class="kx-kv"><span>VAT No.</span><b ${E('vendor_details_full.vat_number')}>{{vendor_details_full.vat_number}}</b></div>
      </div>
      <div class="kx-col-ar" dir="rtl">
        <div class="kx-name" ${E('customer_data.name_ar')}>{{customer_data.name_ar}}</div>
        <div class="kx-line"></div>
        <div class="kx-kv"><span>السجل التجاري</span><b>{{vendor_details_full.crn}}</b></div>
        <div class="kx-kv"><span>الرقم الضريبي</span><b>{{vendor_details_full.vat_number}}</b></div>
      </div>
    </div>
  </div>
</div>

<div class="kx-meta" data-block="meta" data-label="Invoice details">
  <div class="kx-meta-item"><span class="kx-meta-k">Invoice Date<i class="kx-ar">تاريخ الفاتورة</i></span><span class="kx-meta-v" ${E('date')}>{{date}}</span></div>
  <div class="kx-meta-item"><span class="kx-meta-k">Supply Date<i class="kx-ar">تاريخ التوريد</i></span><span class="kx-meta-v" ${E('supply_date')}>{{supply_date}}</span></div>
  <div class="kx-meta-item"><span class="kx-meta-k">Due Date<i class="kx-ar">تاريخ الاستحقاق</i></span><span class="kx-meta-v" ${E('due_date')}>{{due_date}}</span></div>
  <div class="kx-meta-item"><span class="kx-meta-k">Invoice Period<i class="kx-ar">فترة الفاتورة</i></span><span class="kx-meta-v"><span ${E('period_from')}>{{period_from}}</span> – <span ${E('period_to')}>{{period_to}}</span></span></div>
  <div class="kx-meta-item"><span class="kx-meta-k">Contract / PO No.<i class="kx-ar">رقم العقد</i></span><span class="kx-meta-v" ${E('po_number')}>{{po_number}}</span></div>
  <div class="kx-meta-item"><span class="kx-meta-k">Project / Ref.<i class="kx-ar">المرجع</i></span><span class="kx-meta-v" ${E('project_reference')}>{{project_reference}}</span></div>
</div>

<div class="kx-items-wrap" data-block="items" data-label="Line items" data-split>
  <table class="pbd-items kx-items">
    <thead><tr>
      <th class="l">#<i class="kx-ar-h">م</i></th>
      <th class="l">Code (SKU)<i class="kx-ar-h">الرمز</i></th>
      <th class="l">Description / Nature of goods<i class="kx-ar-h">طبيعة السلعة أو الخدمة</i></th>
      <th>Qty<i class="kx-ar-h">الكمية</i></th>
      <th>Unit<i class="kx-ar-h">الوحدة</i></th>
      <th>Unit Rate<i class="kx-ar-h">سعر الوحدة</i></th>
      <th>Discount<i class="kx-ar-h">الخصم</i></th>
      <th>Taxable<i class="kx-ar-h">الخاضع للضريبة</i></th>
      <th>Tax %<i class="kx-ar-h">النسبة</i></th>
      <th>Tax Amt<i class="kx-ar-h">مبلغ الضريبة</i></th>
      <th>Total<i class="kx-ar-h">الإجمالي</i></th>
    </tr></thead>
    <tbody>
      {{#each sales_item_entries}}<tr>
        <td class="l it-no">{{inc @index}}</td>
        <td class="l"><span class="kx-sku" ${E('sales_item_entries.{{@index}}.item_data.sku')}>{{this.item_data.sku}}</span></td>
        <td class="l kx-desc"><b ${E('sales_item_entries.{{@index}}.item_data.name')}>{{this.item_data.name}}</b><span class="kx-ar" ${E('sales_item_entries.{{@index}}.narration')}>{{this.narration}}</span></td>
        <td ${E('sales_item_entries.{{@index}}.quantity', 'num')}>{{this.quantity}}</td>
        <td><span class="kx-unit">{{this.units_of_measurement_data.name}}</span><span class="kx-ar-sm"></span></td>
        <td ${E('sales_item_entries.{{@index}}.rate', 'money')}>{{money this.rate}}</td>
        <td>{{money this.discount_amount}}</td>
        <td>{{money this.gross_amount}}</td>
        <td class="kx-rate">{{this.tax_rate}}</td>
        <td>{{money this.tax_amount}}</td>
        <td><b>{{money this.total_amount}}</b></td>
      </tr>{{/each}}
    </tbody>
  </table>
</div>

<div class="kx-totals" data-block="totals" data-label="Totals & amount in words">
  <div class="kx-tot-grid">
    <div class="kx-tot-row"><span class="kx-tot-en">Gross Total (SAR)</span><span class="kx-tot-v">{{money total_gross_amount}}</span><span class="kx-tot-ar">الإجمالي قبل الضريبة</span></div>
    <div class="kx-tot-row"><span class="kx-tot-en">Total Discount (SAR)</span><span class="kx-tot-v">{{money additional_discount}}</span><span class="kx-tot-ar">إجمالي الخصم</span></div>
    <div class="kx-tot-row"><span class="kx-tot-en">Total Excl. VAT (SAR)</span><span class="kx-tot-v">{{money total_excl_vat}}</span><span class="kx-tot-ar">الإجمالي غير شامل الضريبة</span></div>
    <div class="kx-tot-row"><span class="kx-tot-en">VAT Amount 15% (SAR)</span><span class="kx-tot-v">{{money total_vat}}</span><span class="kx-tot-ar">قيمة ضريبة القيمة المضافة</span></div>
    <div class="kx-tot-row kx-tot-grand"><span class="kx-tot-en">Amount Incl. VAT (SAR)</span><span class="kx-tot-v">{{money total_incl_vat}}</span><span class="kx-tot-ar">المبلغ شامل الضريبة</span></div>
    <div class="kx-tot-row kx-tot-due"><span class="kx-tot-en">Balance Due (SAR)</span><span class="kx-tot-v">{{money amount_due}}</span><span class="kx-tot-ar">الرصيد المستحق</span></div>
  </div>
  <div class="kx-words">
    <div class="kx-words-row"><span class="kx-words-k">Amount in words</span><span class="kx-words-v" ${E('amount_due_words')}>{{amount_due_words}}</span></div>
    <div class="kx-words-row" dir="rtl"><span class="kx-words-k">المبلغ كتابةً</span><span class="kx-words-v"></span></div>
  </div>
</div>

{{#if showBank}}<div class="kx-bankqr" data-block="bank" data-label="QR &amp; bank details">
  {{#if showQr}}<div class="kx-qr">
    <img src="{{qr_code}}" alt="ZATCA QR" />
    <div class="kx-qr-cap">Scan to verify<span class="kx-ar">امسح للتحقق من الفاتورة</span></div>
  </div>{{/if}}
  <div class="kx-bank">
    <div class="kx-bank-h"><span>Bank Details</span><span class="kx-ar">تفاصيل الحساب البنكي</span></div>
    <table class="kx-bank-tbl">
      <tr><td class="kx-bank-k">Account Name<i class="kx-ar">اسم الحساب</i></td><td ${E('bank_details.name')}>{{bank_details.name}}</td></tr>
      <tr><td class="kx-bank-k">Bank<i class="kx-ar">البنك</i></td><td ${E('bank_details.bank_name')}>{{bank_details.bank_name}}</td></tr>
      <tr><td class="kx-bank-k">Branch<i class="kx-ar">الفرع</i></td><td ${E('bank_details.branch_code')}>{{bank_details.branch_code}}</td></tr>
      <tr><td class="kx-bank-k">Account No.<i class="kx-ar">رقم الحساب</i></td><td ${E('bank_details.account_number')}>{{bank_details.account_number}}</td></tr>
      <tr><td class="kx-bank-k">IBAN<i class="kx-ar">الآيبان</i></td><td ${E('bank_details.iban_code')}>{{bank_details.iban_code}}</td></tr>
      <tr><td class="kx-bank-k">SWIFT<i class="kx-ar">سويفت</i></td><td ${E('bank_details.swift_number')}>{{bank_details.swift_number}}</td></tr>
    </table>
  </div>
</div>{{/if}}`,

    delivery:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype">Delivery Note</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref" ${E('doc.date')}>{{doc.date}}</div>
    <div class="pbd-ref">Inv ref {{doc.invoice}}</div>
  </div>
</div>

<div class="pbd-parties" data-block="parties" data-label="Parties">
  <div class="pbd-party">
    <div class="pbd-party-h">Ship to</div>
    <div class="pbd-party-name" ${E('shipTo.name')}>{{shipTo.name}}</div>
    <div class="pbd-party-line">Attn: <span ${E('shipTo.attn')}>{{shipTo.attn}}</span></div>
    <div class="pbd-party-line" ${E('shipTo.line1')}>{{shipTo.line1}}</div>
    <div class="pbd-party-line" ${E('shipTo.line2')}>{{shipTo.line2}}</div>
  </div>
  ${PARTY_CUST('Customer')}
  <div class="pbd-party">
    <div class="pbd-party-h">Despatch</div>
    <div class="pbd-party-line">Vehicle: <span ${E('doc.vehicle')}>{{doc.vehicle}}</span></div>
    <div class="pbd-party-line">Driver: <span ${E('doc.driver')}>{{doc.driver}}</span></div>
    <div class="pbd-party-line">PO: <span ${E('doc.po')}>{{doc.po}}</span></div>
  </div>
</div>

<div class="pbd-items-wrap" data-block="items" data-label="Line items" data-split>
  <table class="pbd-items">
    <thead><tr><th class="l">#</th><th class="l">Code</th><th class="l">Description</th><th>Unit</th><th>Qty ordered</th><th>Qty delivered</th></tr></thead>
    <tbody>
      {{#each items}}<tr>
        <td class="l it-no">{{@number}}</td>
        <td class="l"><span class="it-sku" style="font-size:9px" ${E('items.{{@index}}.sku')}>{{this.sku}}</span></td>
        <td class="l it-desc"><b ${E('items.{{@index}}.name')}>{{this.name}}</b><span class="it-sku" ${E('items.{{@index}}.detail')}>{{this.detail}}</span></td>
        <td ${E('items.{{@index}}.unit')}>{{this.unit}}</td>
        <td ${E('items.{{@index}}.qty', 'num')}>{{qty this.qty}}</td>
        <td><b ${E('items.{{@index}}.delivered', 'num')}>{{qty this.delivered}}</b></td>
      </tr>{{/each}}
    </tbody>
  </table>
</div>

<div class="pbd-cols" data-block="notes" data-label="Notes">
  <div>
    <div class="pbd-notes-h">Goods received in good condition</div>
    <div class="pbd-notes-body" ${E('notes')}>{{notes}}</div>
  </div>
  <div>
    <div class="pbd-notes-h">Discrepancies noted</div>
    <div class="pbd-notes-body" style="min-height:36px;border:1px dashed var(--pbd-line-strong);border-radius:4px"></div>
  </div>
</div>

<div class="pbd-sign" data-block="sign" data-label="Signature">
  <div class="pbd-sign-line"><div class="pbd-sign-rule"><b>Received by (name &amp; signature)</b>Date &amp; company stamp</div></div>
  <div class="pbd-sign-line"><div class="pbd-sign-rule"><b ${E('doc.driver')}>{{doc.driver}}</b>Delivered by</div></div>
</div>

${FOOT}`,

    po:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype">Purchase Order</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref" ${E('doc.date')}>{{doc.date}}</div>
    <span class="pbd-status pbd-status--draft">Required by {{doc.requiredBy}}</span>
  </div>
</div>

<div class="pbd-parties pbd-parties--2" data-block="parties" data-label="Supplier">
  <div class="pbd-party">
    <div class="pbd-party-h">Supplier</div>
    <div class="pbd-party-name" ${E('vendor.name')}>{{vendor.name}}</div>
    <div class="pbd-party-line">Attn: <span ${E('vendor.attn')}>{{vendor.attn}}</span></div>
    <div class="pbd-party-line" ${E('vendor.line1')}>{{vendor.line1}}</div>
    <div class="pbd-party-line" ${E('vendor.line2')}>{{vendor.line2}}</div>
    <div class="pbd-party-line">TRN <span ${E('vendor.trn')}>{{vendor.trn}}</span></div>
  </div>
  <div class="pbd-party">
    <div class="pbd-party-h">Deliver to</div>
    <div class="pbd-party-name" ${E('shipTo.name')}>{{shipTo.name}}</div>
    <div class="pbd-party-line">Attn: <span ${E('shipTo.attn')}>{{shipTo.attn}}</span></div>
    <div class="pbd-party-line" ${E('shipTo.line1')}>{{shipTo.line1}}</div>
    <div class="pbd-party-line" ${E('shipTo.line2')}>{{shipTo.line2}}</div>
  </div>
</div>

<div class="pbd-meta pbd-meta--3" data-block="meta" data-label="Meta">
  <div class="pbd-meta-item"><span class="pbd-meta-k">PO number</span><span class="pbd-meta-v" ${E('doc.number')}>{{doc.number}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Order date</span><span class="pbd-meta-v" ${E('doc.date')}>{{doc.date}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Required by</span><span class="pbd-meta-v" ${E('doc.requiredBy')}>{{doc.requiredBy}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Buyer</span><span class="pbd-meta-v" ${E('doc.buyer')}>{{doc.buyer}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Incoterms</span><span class="pbd-meta-v" ${E('doc.incoterms')}>{{doc.incoterms}}</span></div>
  <div class="pbd-meta-item"><span class="pbd-meta-k">Currency</span><span class="pbd-meta-v" ${E('currency')}>{{currency}}</span></div>
</div>

<div class="pbd-items-wrap" data-block="items" data-label="Line items" data-split>
  <table class="pbd-items">
    ${ITEMS_HEAD}
    <tbody>
      {{#each items}}${ITEMS_ROW}{{/each}}
    </tbody>
  </table>
</div>

${TOTALS}

${NOTES('Purchasing terms')}

${SIGN}

${FOOT}`,

    report:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype" style="font-size:17px;text-transform:none;line-height:1.2;max-width:280px;margin-left:auto" ${E('doc.title')}>{{doc.title}}</div>
    <div class="pbd-ref" ${E('doc.period')}>{{doc.period}}</div>
    <div class="pbd-ref">{{doc.basis}} basis · {{currency}}</div>
  </div>
</div>

{{#if showKpis}}<div class="pbd-kpis" data-block="kpis" data-label="KPIs">
  {{#each kpis}}<div class="pbd-kpi">
    <div class="pbd-kpi-l" ${E('kpis.{{@index}}.label')}>{{this.label}}</div>
    <div class="pbd-kpi-v" ${E('kpis.{{@index}}.value')}>{{this.value}}</div>
    <div class="pbd-kpi-s" ${E('kpis.{{@index}}.sub')}>{{this.sub}}</div>
  </div>{{/each}}
</div>{{/if}}

<div class="pbd-h" data-block="h" data-label="Section title">Statement detail</div>

<div class="pbd-items-wrap" data-block="items" data-label="Statement rows" data-split style="margin-top:6px">
  <table class="pbd-items">
    <thead><tr><th class="l">Line item</th><th>Current period</th><th>Prior period</th><th>% of revenue</th></tr></thead>
    <tbody>
      {{#each rows}}<tr class="is-{{this.kind}}">
        <td class="l" ${E('rows.{{@index}}.label')}>{{this.label}}</td>
        <td>{{money this.current}}</td>
        <td>{{money this.prior}}</td>
        <td>{{pct this.pctRevenue}}</td>
      </tr>{{/each}}
    </tbody>
  </table>
</div>

<div class="pbd-cols" data-block="notes" data-label="Notes">
  <div>
    <div class="pbd-notes-h">Basis of preparation</div>
    <div class="pbd-notes-body" ${E('notes')}>{{notes}}</div>
  </div>
  <div>
    <div class="pbd-notes-h">Prepared by</div>
    <div class="pbd-notes-body"><b ${E('signatory.name')}>{{signatory.name}}</b><br><span ${E('signatory.role')}>{{signatory.role}}</span><br>{{doc.generated}}</div>
  </div>
</div>

${FOOT}`,

    receipt:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype">Receipt</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref" ${E('doc.date')}>{{doc.date}}</div>
    <span class="pbd-status pbd-status--paid">Payment received</span>
  </div>
</div>

<div class="pbd-parties pbd-parties--2" data-block="parties" data-label="Received from">
  ${PARTY_CUST('Received from')}
  <div class="pbd-party">
    <div class="pbd-party-h">Payment</div>
    <div class="pbd-party-line">Method: <b ${E('doc.method')}>{{doc.method}}</b></div>
    <div class="pbd-party-line">Reference: <span ${E('doc.reference')}>{{doc.reference}}</span></div>
    <div class="pbd-party-line">Date: <span ${E('doc.date')}>{{doc.date}}</span></div>
  </div>
</div>

<div class="pbd-h" data-block="h" data-label="Section title">Allocated to invoices</div>

<div class="pbd-items-wrap" data-block="items" data-label="Allocations" data-split style="margin-top:6px">
  <table class="pbd-items">
    <thead><tr><th class="l">#</th><th class="l">Invoice</th><th class="l">Date</th><th>Invoice total</th><th>Amount applied</th></tr></thead>
    <tbody>
      {{#each allocations}}<tr>
        <td class="l it-no">{{@number}}</td>
        <td class="l"><b ${E('allocations.{{@index}}.invoice')}>{{this.invoice}}</b></td>
        <td class="l" ${E('allocations.{{@index}}.date')}>{{this.date}}</td>
        <td>{{money this.invoiceTotal}}</td>
        <td><b>{{money this.applied}}</b></td>
      </tr>{{/each}}
    </tbody>
  </table>
</div>

<div class="pbd-totals-wrap" data-block="totals" data-label="Totals">
  <div class="pbd-totals">
    <div class="pbd-tot-grand"><span class="pbd-tot-k">Amount received</span><span class="pbd-tot-v">{{money received}}</span></div>
    <div class="pbd-tot-row pbd-tot-row--sub" style="margin-top:6px"><span>Balance remaining</span><span class="pbd-tot-v">{{money balance}}</span></div>
    <div class="pbd-amountwords">In words: <b ${E('words')}>{{words}}</b></div>
  </div>
</div>

<div class="pbd-cols" data-block="notes" data-label="Notes">
  <div><div class="pbd-notes-h">Notes</div><div class="pbd-notes-body" ${E('notes')}>{{notes}}</div></div>
  <div></div>
</div>

${SIGN}

${FOOT}`,

    credit:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype">Credit Note</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref" ${E('doc.date')}>{{doc.date}}</div>
    <div class="pbd-ref">Against <b ${E('doc.against')}>{{doc.against}}</b></div>
  </div>
</div>

<div class="pbd-parties pbd-parties--2" data-block="parties" data-label="Bill to">
  ${PARTY_CUST('Credit to')}
  <div class="pbd-party">
    <div class="pbd-party-h">Reason for credit</div>
    <div class="pbd-party-name" style="font-size:11px" ${E('doc.reason')}>{{doc.reason}}</div>
    <div class="pbd-party-line">Original invoice: {{doc.against}}</div>
  </div>
</div>

<div class="pbd-items-wrap" data-block="items" data-label="Credit lines" data-split>
  <table class="pbd-items">
    ${ITEMS_HEAD}
    <tbody>
      {{#each items}}${ITEMS_ROW}{{/each}}
    </tbody>
  </table>
</div>

${TOTALS}

${NOTES('Notes')}

${SIGN}

${FOOT}`,

    statement:
`${LETTERHEAD}
  <div class="pbd-lh-doc">
    <div class="pbd-doctype" style="font-size:19px">Statement</div>
    <div class="pbd-ref">No. <b ${E('doc.number')}>{{doc.number}}</b></div>
    <div class="pbd-ref">As at <span ${E('doc.date')}>{{doc.date}}</span></div>
  </div>
</div>

<div class="pbd-parties pbd-parties--2" data-block="parties" data-label="Account">
  ${PARTY_CUST('Account')}
  <div class="pbd-party">
    <div class="pbd-party-h">Statement period</div>
    <div class="pbd-party-name" style="font-size:11px" ${E('doc.period')}>{{doc.period}}</div>
    <div class="pbd-party-line">Currency: {{currency}}</div>
    <div class="pbd-party-line">Opening balance: {{money doc.openingBalance}}</div>
  </div>
</div>

<div class="pbd-items-wrap" data-block="items" data-label="Ledger" data-split>
  <table class="pbd-items">
    <thead><tr><th class="l">Date</th><th class="l">Reference</th><th class="l">Type</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead>
    <tbody>
      {{#each ledger}}<tr>
        <td class="l">{{this.date}}</td>
        <td class="l"><b>{{this.ref}}</b></td>
        <td class="l it-tax">{{this.type}}</td>
        <td>{{#if this.debit}}{{money this.debit}}{{else}}—{{/if}}</td>
        <td class="it-disc">{{#if this.credit}}{{money this.credit}}{{else}}—{{/if}}</td>
        <td><b>{{money this.balance}}</b></td>
      </tr>{{/each}}
    </tbody>
  </table>
</div>

<div class="pbd-totals-wrap" data-block="totals" data-label="Aging & balance">
  <div class="pbd-totals" style="width:420px">
    <div class="pbd-tot-row"><span>Current</span><span class="pbd-tot-v">{{money aging.current}}</span></div>
    <div class="pbd-tot-row"><span>1–30 days</span><span class="pbd-tot-v">{{money aging.d30}}</span></div>
    <div class="pbd-tot-row"><span>31–60 days</span><span class="pbd-tot-v">{{money aging.d60}}</span></div>
    <div class="pbd-tot-row"><span>61–90 days</span><span class="pbd-tot-v">{{money aging.d90}}</span></div>
    <div class="pbd-tot-row pbd-tot-row--sub"><span>Over 90 days</span><span class="pbd-tot-v">{{money aging.over}}</span></div>
    <div class="pbd-tot-grand"><span class="pbd-tot-k">Closing balance due</span><span class="pbd-tot-v">{{money closingBalance}}</span></div>
  </div>
</div>

<div class="pbd-cols" data-block="notes" data-label="Notes">
  <div><div class="pbd-notes-h">Notes</div><div class="pbd-notes-body" ${E('notes')}>{{notes}}</div></div>
  <div></div>
</div>

${FOOT}`,
  };

  export const PBTemplates = TEMPLATES;

